<template>
  <div class="weapons-page">
    <!-- Hero -->
    <div class="hero">
      <div class="hero-overlay">
        <div class="hero-content">
          <h1 class="hero-title">{{ $t('weapons.title') }}</h1>
        </div>
      </div>
    </div>

    <div class="page-content">
      <!-- Tabs -->
      <div class="tabs-wrap">
        <div class="tabs">
          <button
            v-for="category in weapons"
            :key="category.type"
            class="tab"
            :class="{ active: tab === category.type }"
            @click="tab = category.type"
          >
            {{ formatName(category.type) }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrap" style="margin-bottom: 3rem;">
        <table class="weapon-table">
          <thead>
            <tr>
              <th>{{ $t('weapons.col.name') }}</th>
              <th>{{ $t('weapons.col.damage') }}</th>
              <th>{{ $t('weapons.col.properties') }}</th>
              <th>{{ $t('weapons.col.mastery') }}</th>
              <th>{{ $t('weapons.col.weight') }}</th>
              <th>{{ $t('weapons.col.cost') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="weapon in currentItems"
              :key="weapon.name"
              class="weapon-row"
            >
              <td class="weapon-name">{{ $lf(weapon, 'name') }}</td>
              <td>
                <span class="damage-badge" v-if="weapon.damage">{{
                  weapon.damage
                }}</span>
              </td>
              <td class="properties-cell">{{ $lf(weapon, 'properties') }}</td>
              <td>
                <span class="mastery-badge" v-if="weapon.mastery">{{
                  $lf(weapon, 'mastery')
                }}</span>
              </td>
              <td>{{ weapon.weight }}</td>
              <td>{{ weapon.cost }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Glossary -->
      <div class="glossary-grid">
        <div class="glossary-group">
          <h2 class="glossary-title">{{ $t('weapons.propertiesTitle') }}</h2>
          <div class="glossary-list">
            <div
              v-for="entry in glossary.properties"
              :key="entry.name"
              class="glossary-card"
            >
              <span class="glossary-name">{{ $lf(entry, 'name') }}</span>
              <p class="glossary-desc">{{ $lf(entry, 'desc') }}</p>
            </div>
          </div>
        </div>

        <div class="glossary-group">
          <h2 class="glossary-title">{{ $t('weapons.masteriesTitle') }}</h2>
          <div class="glossary-list">
            <div
              v-for="entry in glossary.masteries"
              :key="entry.name"
              class="glossary-card"
            >
              <span class="glossary-name">{{ $lf(entry, 'name') }}</span>
              <p class="glossary-desc">{{ $lf(entry, 'desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import weapons from "./weapons.json";
import glossary from "./weaponGlossary.json";

export default {
  data() {
    return {
      tab: weapons[0].type,
      weapons,
      glossary,
    };
  },

  computed: {
    currentItems() {
      return this.weapons.find((w) => w.type === this.tab)?.items ?? [];
    },
  },

  methods: {
    formatName(type) {
      return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
  },
};
</script>

<style scoped>
/* ── Page ───────────────────────────────────────── */
.weapons-page {
  min-height: 100vh;
  background: var(--bg-page);
  color: var(--text-body);
}

/* ── Hero ───────────────────────────────────────── */
.hero {
  position: relative;
  width: 100%;
  height: 220px;
  background: linear-gradient(
    135deg,
    rgba(var(--accent-rgb), 0.15) 0%,
    rgba(var(--bg-page-rgb), 0.95) 100%
  );
  border-bottom: 1px solid rgba(var(--accent-rgb), 0.2);
  overflow: hidden;
}
.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      circle at 20% 50%,
      rgba(var(--accent-rgb), 0.08) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(var(--accent-rgb), 0.05) 0%,
      transparent 40%
    );
}
.hero-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 2rem 2.5rem;
}
.hero-content {
  max-width: 700px;
}
.hero-eyebrow {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--accent);
  margin-bottom: 0.4rem;
}
.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 0.4rem;
  line-height: 1.1;
}
.hero-subtitle {
  font-size: 0.95rem;
  color: var(--text-muted);
  margin: 0;
}

/* ── Page content ───────────────────────────────── */
.page-content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
}

/* ── Tabs ───────────────────────────────────────── */
.tabs-wrap {
  overflow-x: auto;
  margin-bottom: 1.25rem;
  padding-bottom: 2px;
}
.tabs {
  display: flex;
  gap: 0.35rem;
  min-width: max-content;
}
.tab {
  padding: 6px 16px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  border-radius: 999px;
  border: 1px solid var(--border-light);
  background: transparent;
  color: var(--text-subtle);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.tab:hover {
  border-color: rgba(var(--accent-rgb), 0.3);
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.06);
}
.tab.active {
  background: rgba(var(--accent-rgb), 0.15);
  border-color: rgba(var(--accent-rgb), 0.4);
  color: var(--accent);
}

/* ── Table ──────────────────────────────────────── */
.table-wrap {
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  overflow: hidden;
  overflow-x: auto;
}
.weapon-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.weapon-table thead tr {
  background: rgba(var(--accent-rgb), 0.1);
}
.weapon-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--accent);
  white-space: nowrap;
}
.weapon-row {
  border-bottom: 1px solid var(--border-faint);
  transition: background 0.15s ease;
}
.weapon-row:last-child {
  border-bottom: none;
}
.weapon-row:hover {
  background: rgba(192, 132, 252, 0.05);
}
.weapon-table td {
  padding: 0.65rem 1rem;
  color: var(--text-muted);
  vertical-align: middle;
}
.weapon-name {
  color: var(--text-heading) !important;
  font-weight: 500;
  white-space: nowrap;
}
.properties-cell {
  font-size: 0.8rem;
  max-width: 250px;
}

/* Damage badge */
.damage-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(251, 146, 60, 0.1);
  color: #fb923c;
  border: 1px solid rgba(251, 146, 60, 0.3);
  white-space: nowrap;
}

/* Mastery badge */
.mastery-badge {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--accent);
  border: 1px solid rgba(var(--accent-rgb), 0.2);
  white-space: nowrap;
}

/* ── Glossary ───────────────────────────────────── */
.glossary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}
.glossary-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--accent);
  margin: 0 0 1rem;
}
.glossary-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.glossary-card {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  background: rgba(var(--accent-rgb), 0.03);
}
.glossary-name {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 0.3rem;
}
.glossary-desc {
  font-size: 0.78rem;
  color: var(--text-muted);
  line-height: 1.55;
  margin: 0;
}

/* ── Mobile ─────────────────────────────────────── */
@media (max-width: 640px) {
  .hero {
    height: 180px;
  }
  .hero-title {
    font-size: 1.75rem;
  }
  .hero-overlay {
    padding: 1.25rem 1.5rem;
  }
  .glossary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
